/**
 * Perspectiva da marcação: o retângulo do logo levado para os quatro cantos
 * que o vendedor arrastou sobre a foto. É a mesma homografia que o backend
 * aplica (ProductMock/Perspective) — a prévia e a amostra precisam dar a mesma
 * conta, senão o logo sai num lugar e a tela mostra outro.
 *
 * Cantos sempre na ordem do logo: superior esquerdo, superior direito,
 * inferior direito, inferior esquerdo.
 */

const resolver = (m) => {
    const n = m.length;

    for (let col = 0; col < n; col++) {
        let pivo = col;
        for (let row = col + 1; row < n; row++) {
            if (Math.abs(m[row][col]) > Math.abs(m[pivo][col])) pivo = row;
        }
        if (Math.abs(m[pivo][col]) < 1e-12) return null;
        [m[col], m[pivo]] = [m[pivo], m[col]];

        for (let row = 0; row < n; row++) {
            if (row === col) continue;
            const fator = m[row][col] / m[col][col];
            for (let k = col; k <= n; k++) m[row][k] -= fator * m[col][k];
        }
    }

    return m.map((linha, i) => linha[n] / linha[i]);
};

/** Matriz 3x3 (h33 = 1) que leva os pontos `de` para os pontos `para`. */
export const homografia = (de, para) => {
    const linhas = [];

    for (let k = 0; k < 4; k++) {
        const [x, y] = de[k];
        const [X, Y] = para[k];
        linhas.push([x, y, 1, 0, 0, 0, -x * X, -y * X, X]);
        linhas.push([0, 0, 0, x, y, 1, -x * Y, -y * Y, Y]);
    }

    const h = resolver(linhas);

    return h && [[h[0], h[1], h[2]], [h[3], h[4], h[5]], [h[6], h[7], 1]];
};

/**
 * `transform` que desenha um elemento de largura x altura (origem no canto
 * superior esquerdo) sobre os cantos em px. O matrix3d é coluna a coluna.
 */
export const matrizCss = (largura, altura, cantos) => {
    const h = homografia([[0, 0], [largura, 0], [largura, altura], [0, altura]], cantos);
    if (!h) return undefined;

    const [[a, b, c], [d, e, f], [g, i]] = h;

    return `matrix3d(${[a, d, 0, g, b, e, 0, i, 0, 0, 1, 0, c, f, 0, 1].join(',')})`;
};

/**
 * Quadrilátero que dá para imprimir. Canto cruzado vira gravatinha, e a
 * homografia dela espelha a arte — o backend recusa, então a tela nem deixa.
 */
export const convexo = (cantos) => {
    let sinal = 0;

    for (let k = 0; k < 4; k++) {
        const [ax, ay] = cantos[k];
        const [bx, by] = cantos[(k + 1) % 4];
        const [cx, cy] = cantos[(k + 2) % 4];
        const cruz = (bx - ax) * (cy - by) - (by - ay) * (cx - bx);

        if (Math.abs(cruz) < 1) return false;

        const atual = cruz > 0 ? 1 : -1;
        if (sinal && atual !== sinal) return false;
        sinal = atual;
    }

    return true;
};

/** Os cantos do retângulo girado, em px do palco — o ponto de partida dos pinos. */
export const cantosDaCaixa = ({ cx, cy, w, h }, graus) => {
    const rad = (graus * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    return [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]].map(([x, y]) => [
        cx + x * cos - y * sin,
        cy + x * sin + y * cos
    ]);
};
